import { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Image, GraduationCap, Sparkles, Users, Camera, MessageSquare,
  Building2, MapPin, FileText, LogOut, Inbox, Palette, ExternalLink, Eye, EyeOff, Stethoscope,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import SectionEditor from "@/components/admin/SectionEditor";
import LeadsPanel from "@/components/admin/LeadsPanel";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

const NAV = [
  { key: "leads", label: "Enquiries", icon: Inbox, group: "manage" },
  { key: "branding", label: "Branding", icon: Palette, group: "manage" },
  { key: "hero", label: "Hero Banner", icon: Image, group: "sections" },
  { key: "about", label: "About Us", icon: LayoutDashboard, group: "sections" },
  { key: "courses", label: "Courses", icon: GraduationCap, group: "sections" },
  { key: "consultancy", label: "Medical Consultancy", icon: Stethoscope, group: "sections" },
  { key: "whyChooseUs", label: "Why Choose Us", icon: Sparkles, group: "sections" },
  { key: "faculty", label: "Faculty", icon: Users, group: "sections" },
  { key: "gallery", label: "Gallery", icon: Camera, group: "sections" },
  { key: "testimonials", label: "Testimonials", icon: MessageSquare, group: "sections" },
  { key: "colleges", label: "Colleges", icon: Building2, group: "sections" },
  { key: "contact", label: "Contact", icon: MapPin, group: "sections" },
  { key: "footer", label: "Footer", icon: FileText, group: "sections" },
];

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const { content, setContent, saveContent } = useContent();
  const navigate = useNavigate();
  const [active, setActive] = useState("leads");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (content && !draft) setDraft(content);
  }, [content, draft]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!draft) return <div className="min-h-screen grid place-items-center text-slate-500">Loading content...</div>;

  const setDraftAndDirty = (next) => { setDraft(next); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveContent(draft);
      setDraft(saved);
      setContent(saved);
      setDirty(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-root">
      {/* Sidebar */}
      <aside className="w-72 bg-[#0B1736] text-slate-300 min-h-screen p-5 flex flex-col gap-2 sticky top-0 h-screen overflow-y-auto" data-testid="admin-sidebar">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500 grid place-items-center text-white font-bold">S</div>
          <div>
            <div className="font-extrabold text-white">Sunbright CMS</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Staff Panel</div>
          </div>
        </Link>

        <NavGroup label="Manage" items={NAV.filter(n => n.group === "manage")} active={active} setActive={(k) => { setActive(k); }} draft={draft} />
        <NavGroup label="Sections" items={NAV.filter(n => n.group === "sections")} active={active} setActive={(k) => { setActive(k); }} draft={draft} showVisibility />

        <div className="mt-auto pt-6 border-t border-white/10">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-teal-300 transition py-2 px-3" data-testid="admin-view-site">
            <ExternalLink className="w-4 h-4" /> View Site
          </a>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-slate-300 hover:text-red-300 transition py-2 px-3 w-full" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Logout
          </button>
          <div className="px-3 mt-3 text-[10px] text-slate-500">{user.email}</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        {dirty && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center justify-between" data-testid="unsaved-banner">
            <span>You have unsaved changes.</span>
            <button onClick={save} className="text-xs font-bold underline">Save now</button>
          </div>
        )}
        {active === "leads" ? (
          <LeadsPanel />
        ) : (
          <SectionEditor
            activeKey={active}
            content={draft}
            setContent={setDraftAndDirty}
            onSave={save}
            saving={saving}
          />
        )}
      </main>
    </div>
  );
}

function NavGroup({ label, items, active, setActive, draft, showVisibility }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-3 mb-2">{label}</div>
      <div className="space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          const isActive = active === it.key;
          const sectionVisible = draft.sections?.[it.key]?.visible;
          return (
            <button
              key={it.key}
              onClick={() => setActive(it.key)}
              data-testid={`nav-${it.key}`}
              className={`group w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                isActive ? "bg-teal-500/15 text-white" : "hover:bg-white/5 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-3">
                <Icon className={`w-4 h-4 ${isActive ? "text-teal-300" : ""}`} />
                {it.label}
              </span>
              {showVisibility && (sectionVisible === false ? <EyeOff className="w-3.5 h-3.5 text-slate-500" /> : <Eye className="w-3.5 h-3.5 text-teal-400/70" />)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
